import { useInputControl } from '@conform-to/react'
import { REGEXP_ONLY_DIGITS_AND_CHARS, type OTPInputProps } from 'input-otp'
import React, { useId } from 'react'
import { Checkbox, type CheckboxProps } from './ui/checkbox.tsx'
import { Combobox, type ComboboxProps, type ComboboxOption } from './ui/combobox.tsx'
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from './ui/input-otp.tsx'
import { Input } from './ui/input.tsx'
import { Label } from './ui/label.tsx'
import {
	Select,
	SelectContent,
	SelectTrigger,
	SelectValue,
} from './ui/select.tsx'
import { Textarea } from './ui/textarea.tsx'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

export function ErrorList({
	id,
	errors,
}: {
	errors?: ListOfErrors
	id?: string
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ul id={id} className="flex flex-col gap-1">
			{errorsToRender.map((e) => (
				<li key={e} className="text-foreground-destructive text-[10px]">
					{e}
				</li>
			))}
		</ul>
	)
}

export function Field({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Input
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
			/>
			<div className="px-4 pt-1 pb-1 sm:min-h-[32px] sm:pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function OTPField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: Partial<OTPInputProps & { render: never }>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<InputOTP
				pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
				maxLength={6}
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
			>
				<InputOTPGroup>
					<InputOTPSlot index={0} />
					<InputOTPSlot index={1} />
					<InputOTPSlot index={2} />
				</InputOTPGroup>
				<InputOTPSeparator />
				<InputOTPGroup>
					<InputOTPSlot index={3} />
					<InputOTPSlot index={4} />
					<InputOTPSlot index={5} />
				</InputOTPGroup>
			</InputOTP>
			<div className="px-4 pt-1 pb-1 sm:min-h-[32px] sm:pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Textarea
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...textareaProps}
			/>
			<div className="px-4 pt-1 pb-1 sm:min-h-[32px] sm:pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function SelectField({
	labelProps,
	selectProps,
	triggerProps,
	valueProps,
	contentProps,
	errors,
	children,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	selectProps: Omit<React.ComponentProps<typeof Select>, 'children'>
	triggerProps?: React.ComponentProps<typeof SelectTrigger>
	valueProps?: React.ComponentProps<typeof SelectValue>
	contentProps?: React.ComponentProps<typeof SelectContent>
	errors?: ListOfErrors
	children: React.ReactNode
	className?: string
}) {
	const fallbackId = useId()
	const id =
		triggerProps?.id ?? labelProps.htmlFor ?? selectProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Select {...selectProps}>
				<SelectTrigger
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					{...triggerProps}
				>
					<SelectValue {...valueProps} />
				</SelectTrigger>
				<SelectContent {...contentProps}>{children}</SelectContent>
			</Select>
			<div className="px-4 pt-1 pb-1 sm:min-h-[32px] sm:pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

type ComboboxFieldBaseProps = {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	name: string
	options: ComboboxOption[]
	errors?: ListOfErrors
	className?: string
	hiddenInputProps?: Omit<
		React.InputHTMLAttributes<HTMLInputElement>,
		'name' | 'value' | 'defaultValue' | 'type'
	>
}

type ComboboxFieldSingleProps = ComboboxFieldBaseProps & {
	multiple?: false
	value?: string
	defaultValue?: string
	onValueChange?: (value: string | undefined) => void
	comboboxProps?: Omit<
		Extract<ComboboxProps, { multiple?: false }>,
		'options' | 'value' | 'defaultValue' | 'onValueChange' | 'multiple'
	>
}

type ComboboxFieldMultipleProps = ComboboxFieldBaseProps & {
	multiple: true
	value?: string[]
	defaultValue?: string[]
	onValueChange?: (value: string[]) => void
	comboboxProps?: Omit<
		Extract<ComboboxProps, { multiple: true }>,
		'options' | 'value' | 'defaultValue' | 'onValueChange' | 'multiple'
	>
}

export function ComboboxField(
	props: ComboboxFieldSingleProps | ComboboxFieldMultipleProps,
) {
	const {
		labelProps,
		name,
		options,
		errors,
		className,
		value,
		defaultValue,
		onValueChange,
		comboboxProps,
		hiddenInputProps,
	} = props

	const multiple = props.multiple ?? false

	const fallbackId = useId()
	const resolvedId =
		comboboxProps?.triggerProps?.id ?? labelProps.htmlFor ?? fallbackId
	const errorId = errors?.length ? `${resolvedId}-error` : undefined
	const isControlled = value !== undefined
	const [internalValue, setInternalValue] = React.useState<
		string | string[] | undefined
	>(defaultValue)

	React.useEffect(() => {
		if (!isControlled) {
			setInternalValue(defaultValue)
		}
	}, [defaultValue, isControlled])

	const resolvedValue = isControlled ? value : internalValue

	const handleValueChange = (nextValue: string | string[] | undefined) => {
		if (!isControlled) {
			setInternalValue(nextValue)
		}
		onValueChange?.(nextValue as any)
	}

	const { triggerProps: userTriggerProps, ...restComboboxProps } =
		comboboxProps ?? {}

	const triggerProps: ComboboxProps['triggerProps'] = {
		...userTriggerProps,
		id: userTriggerProps?.id ?? resolvedId,
		'aria-invalid':
			userTriggerProps?.['aria-invalid'] ?? (errorId ? true : undefined),
		'aria-describedby': userTriggerProps?.['aria-describedby'] ?? errorId,
	}

	const { htmlFor, ...restLabelProps } = labelProps

	// Serialize value for hidden input
	const hiddenValue = multiple
		? Array.isArray(resolvedValue)
			? resolvedValue.join(',')
			: ''
		: (resolvedValue as string | undefined) ?? ''

	return (
		<div className={className}>
			<Label htmlFor={htmlFor ?? resolvedId} {...restLabelProps} />
			<input
				type="hidden"
				name={name}
				value={hiddenValue}
				{...hiddenInputProps}
			/>
			{multiple ? (
				<Combobox
					{...(restComboboxProps as Omit<
						Extract<ComboboxProps, { multiple: true }>,
						'multiple' | 'options' | 'value' | 'defaultValue' | 'onValueChange'
					>)}
					multiple={true}
					options={options}
					value={resolvedValue as string[] | undefined}
					defaultValue={defaultValue as string[] | undefined}
					onValueChange={handleValueChange as (value: string[]) => void}
					triggerProps={triggerProps}
				/>
			) : (
				<Combobox
					{...(restComboboxProps as Omit<
						Extract<ComboboxProps, { multiple?: false }>,
						'multiple' | 'options' | 'value' | 'defaultValue' | 'onValueChange'
					>)}
					options={options}
					value={resolvedValue as string | undefined}
					defaultValue={defaultValue as string | undefined}
					onValueChange={handleValueChange as (value: string | undefined) => void}
					triggerProps={triggerProps}
				/>
			)}
			<div className="px-4 pt-1 pb-1 sm:min-h-[32px] sm:pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function CheckboxField({
	labelProps,
	buttonProps,
	errors,
	className,
}: {
	labelProps: React.ComponentProps<'label'>
	buttonProps: CheckboxProps & {
		name: string
		form: string
		value?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
	const { key, defaultChecked, ...checkboxProps } = buttonProps
	const fallbackId = useId()
	const checkedValue = buttonProps.value ?? 'on'
	const input = useInputControl({
		key,
		name: buttonProps.name,
		formId: buttonProps.form,
		initialValue: defaultChecked ? checkedValue : undefined,
	})
	const id = buttonProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<div className="flex gap-2">
				<Checkbox
					{...checkboxProps}
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					checked={input.value === checkedValue}
					onCheckedChange={(state) => {
						input.change(state.valueOf() ? checkedValue : '')
						buttonProps.onCheckedChange?.(state)
					}}
					onFocus={(event) => {
						input.focus()
						buttonProps.onFocus?.(event)
					}}
					onBlur={(event) => {
						input.blur()
						buttonProps.onBlur?.(event)
					}}
					type="button"
				/>
				<label
					htmlFor={id}
					{...labelProps}
					className="text-body-xs text-muted-foreground self-center"
				/>
			</div>
			<div className="px-4 pt-1 pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}
